import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";

import { ApiError, isUnauthorizedError } from "@/lib/api-types";
import { reportError } from "@/lib/report-error";

/**
 * Shared TanStack Query defaults (ARCHITECTURE.md §3). Don't retry on 4xx
 * (client errors won't fix themselves); retry transient 5xx/network a couple
 * times. staleTime is conservative so authenticated data feels fresh. Cache-wide
 * `onError` funnels every failed query/mutation through `reportError`
 * (ENGINEERING.md §6) — unless the query opts out via
 * `meta: { suppressGlobalError: true }` (ambient chrome like the navbar badge
 * poll, where a transient failure shouldn't be reported as an app error).
 */
export function createQueryClient() {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (query.meta?.suppressGlobalError) return;
        // A 401 is an expected auth state (expired/absent session), not an app
        // fault — the UI answers it with a login prompt. Reporting it would
        // just be noise every time a session lapses.
        if (isUnauthorizedError(error)) return;
        reportError(error, { source: "query", queryKey: query.queryKey });
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _vars, _ctx, mutation) => {
        reportError(error, {
          source: "mutation",
          mutationKey: mutation.options.mutationKey,
        });
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          if (
            error instanceof ApiError &&
            error.status >= 400 &&
            error.status < 500
          ) {
            return false;
          }
          return failureCount < 2;
        },
      },
      mutations: {
        retry: false,
      },
    },
  });
}
